import React from "react";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ArrowLeft, 
  TrendingUp, 
  ExternalLink, 
  Sparkles, 
  Target, 
  Users, 
  DollarSign, 
  RefreshCw,
  Clock,
  Zap,
  CheckCircle2,
  Rocket,
  Megaphone,
  BarChart3,
  Lightbulb,
  Package
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";

interface Startup {
  id: string;
  title: string;
  slug: string;
  short_description?: string;
  full_description?: string;
  category?: string;
  tags: string[];
  source?: string;
  source_url?: string;
  trend_score: number;
  trend_curve: Array<{ date: string; value: number }>;
  created_at: string;
}

interface MarketInsights {
  target_audience: string;
  growth_potential: string;
  competitors: string;
  monetization: string;
}

interface BuildPhase {
  phase_name: string;
  description: string;
  steps: Array<{ title: string; description: string }>;
}

const StartupProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [startup, setStartup] = useState<Startup | null>(null);
  const [relatedStartups, setRelatedStartups] = useState<Startup[]>([]);
  const [marketInsights, setMarketInsights] = useState<MarketInsights | null>(null);
  const [buildRoadmap, setBuildRoadmap] = useState<BuildPhase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [isLoadingRoadmap, setIsLoadingRoadmap] = useState(false);

  useEffect(() => {
    fetchStartup();
  }, [slug]);

  const fetchStartup = async () => {
    if (!slug) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('startups')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      
      const typedData: Startup = {
        ...data,
        trend_curve: Array.isArray(data.trend_curve) ? data.trend_curve as Array<{ date: string; value: number }> : []
      };
      
      setStartup(typedData);
      
      if (typedData.category) {
        fetchRelatedStartups(typedData.category, typedData.id);
      }
      
      generateMarketInsights(slug);
      generateBuildRoadmap(slug);
    } catch (error) {
      console.error('Error fetching startup:', error);
      toast.error('Failed to load startup details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedStartups = async (category: string, currentId: string) => {
    try {
      const { data, error } = await supabase
        .from('startups')
        .select('*')
        .eq('category', category)
        .neq('id', currentId)
        .order('trend_score', { ascending: false })
        .limit(4);

      if (error) throw error;
      
      const typedData = data.map(item => ({
        ...item,
        trend_curve: Array.isArray(item.trend_curve) ? item.trend_curve as Array<{ date: string; value: number }> : []
      }));
      
      setRelatedStartups(typedData);
    } catch (error) {
      console.error('Error fetching related startups:', error);
    }
  };

  const generateMarketInsights = async (startupSlug: string) => {
    setIsLoadingInsights(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-startup-profile', {
        body: { slug: startupSlug, type: 'insights' }
      });

      if (error) throw error;
      if (data?.marketInsights) {
        setMarketInsights(data.marketInsights);
      }
    } catch (error) {
      console.error('Error generating market insights:', error);
      toast.error('Failed to generate market insights');
    } finally {
      setIsLoadingInsights(false);
    }
  };

  const generateBuildRoadmap = async (startupSlug: string) => {
    setIsLoadingRoadmap(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-startup-profile', {
        body: { slug: startupSlug, type: 'roadmap' }
      });

      if (error) throw error;
      if (data?.buildRoadmap?.phases) {
        setBuildRoadmap(data.buildRoadmap.phases);
      }
    } catch (error) {
      console.error('Error generating build roadmap:', error);
      toast.error('Failed to generate build roadmap');
    } finally {
      setIsLoadingRoadmap(false);
    }
  };

  const handleRegenerateRoadmap = () => {
    if (!slug) return;
    toast.info('Regenerating roadmap...');
    generateBuildRoadmap(slug);
  };

  const handleBuildWithAI = () => {
    navigate('/code-workspace', { state: { startupId: startup?.id, startupTitle: startup?.title } });
  };

  // Derive difficulty and time-to-MVP from trend score
  const getDifficulty = (score: number) => {
    if (score >= 75) return { label: "Beginner-friendly", color: "text-green-600" };
    if (score >= 50) return { label: "Moderate", color: "text-yellow-600" };
    return { label: "Advanced", color: "text-orange-600" };
  };

  const getTimeToMVP = (score: number) => {
    if (score >= 75) return "2-4 weeks";
    if (score >= 50) return "4-8 weeks";
    return "8-12 weeks";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-16 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
            <div>
              <Skeleton className="h-80 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">Startup not found</h1>
          <p className="text-muted-foreground">The startup you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/startups')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Ideas
          </Button>
        </div>
      </div>
    );
  }

  const trendIndicator = startup.trend_score >= 75 
    ? { icon: "🔥", label: "Hot", color: "text-red-500", bg: "bg-red-50" }
    : startup.trend_score >= 60 
    ? { icon: "📈", label: "Rising", color: "text-orange-500", bg: "bg-orange-50" }
    : startup.trend_score >= 40
    ? { icon: "💡", label: "Emerging", color: "text-yellow-600", bg: "bg-yellow-50" }
    : { icon: "🌱", label: "New", color: "text-green-600", bg: "bg-green-50" };

  const difficulty = getDifficulty(startup.trend_score);
  const timeToMVP = getTimeToMVP(startup.trend_score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/startups')}
          className="gap-2 mb-6 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Ideas
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Hero Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${trendIndicator.bg} ${trendIndicator.color}`}>
                  <span>{trendIndicator.icon}</span>
                  {trendIndicator.label}
                </span>
                {startup.category && (
                  <Badge variant="secondary" className="font-normal">
                    {startup.category}
                  </Badge>
                )}
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                {startup.title}
              </h1>
              
              {startup.short_description && (
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {startup.short_description}
                </p>
              )}

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {startup.tags?.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="font-normal text-muted-foreground">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Why This Works Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Why This Works</h2>
              </div>
              <Card className="p-6 space-y-4 border-border/50">
                {isLoadingInsights ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                ) : marketInsights ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <Users className="w-4 h-4" />
                        Target Audience
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {marketInsights.target_audience}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <TrendingUp className="w-4 h-4" />
                        Growth Potential
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {marketInsights.growth_potential}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <BarChart3 className="w-4 h-4" />
                        Competitive Landscape
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        {marketInsights.competitors}
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground">Analyzing market opportunity...</p>
                )}
              </Card>
            </section>

            {/* Interest Trend */}
            {startup.trend_curve && startup.trend_curve.length > 0 && (
              <section className="space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">Interest Over Time</h2>
                </div>
                <Card className="p-6 border-border/50">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={startup.trend_curve}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                      <XAxis 
                        dataKey="date" 
                        className="text-xs"
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis 
                        className="text-xs" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 5, fill: 'hsl(var(--primary))' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </section>
            )}

            {/* MVP Scope Section */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">MVP Scope</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRegenerateRoadmap}
                  disabled={isLoadingRoadmap}
                  className="gap-2 text-muted-foreground"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingRoadmap ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <Card className="p-6 border-border/50">
                {isLoadingRoadmap ? (
                  <div className="space-y-4">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : buildRoadmap.length > 0 ? (
                  <Accordion type="single" collapsible className="w-full">
                    {buildRoadmap.map((phase, idx) => (
                      <AccordionItem key={idx} value={`phase-${idx}`} className="border-border/50">
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <span className="font-medium">{phase.phase_name}</span>
                            </div>
                            <p className="text-sm text-muted-foreground font-normal mt-1 ml-8">
                              {phase.description}
                            </p>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 ml-8 pt-2">
                            {phase.steps.map((step, stepIdx) => (
                              <div key={stepIdx} className="flex gap-3 items-start">
                                <CheckCircle2 className="w-4 h-4 text-primary/60 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="font-medium text-sm">{step.title}</p>
                                  <p className="text-muted-foreground text-sm mt-0.5">
                                    {step.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <p className="text-muted-foreground">Creating your roadmap...</p>
                )}
              </Card>
            </section>

            {/* Business Model Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Business Model</h2>
              </div>
              <Card className="p-6 border-border/50">
                {isLoadingInsights ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ) : marketInsights?.monetization ? (
                  <p className="text-muted-foreground leading-relaxed">
                    {marketInsights.monetization}
                  </p>
                ) : (
                  <p className="text-muted-foreground">Analyzing revenue opportunities...</p>
                )}
              </Card>
            </section>

            {/* Go-To-Market Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Go-To-Market</h2>
              </div>
              <Card className="p-6 border-border/50">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm">Launch Channels</h3>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Product Hunt launch
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Social media presence
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Community engagement
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-medium text-sm">Early Traction</h3>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Beta user program
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Content marketing
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                        Strategic partnerships
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </section>

            {/* Launch Checklist Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2">
                <Rocket className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Launch Checklist</h2>
              </div>
              <Card className="p-6 border-border/50">
                <div className="space-y-3">
                  {[
                    "Core MVP features complete",
                    "Landing page with clear value proposition",
                    "Basic analytics tracking",
                    "User feedback mechanism",
                    "Payment integration (if applicable)",
                    "Launch announcement prepared"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <div className="w-5 h-5 rounded border border-border flex items-center justify-center">
                        <span className="text-muted-foreground text-xs">{idx + 1}</span>
                      </div>
                      <span className="text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </section>

            {/* Related Startups */}
            {relatedStartups.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-xl font-semibold">Similar Ideas</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {relatedStartups.map((related) => (
                    <Card 
                      key={related.id} 
                      className="p-4 cursor-pointer hover:shadow-md transition-all border-border/50 hover:border-border"
                      onClick={() => navigate(`/startup/${related.slug}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium line-clamp-1">{related.title}</h3>
                        <Badge variant="secondary" className="text-xs font-normal ml-2 flex-shrink-0">
                          {related.trend_score}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {related.short_description}
                      </p>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Source Attribution */}
            {startup.source_url && (
              <div className="pt-4 border-t border-border/50">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => window.open(startup.source_url, '_blank')}
                  className="gap-1.5 text-muted-foreground hover:text-foreground p-0 h-auto"
                >
                  View original source
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          {/* Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-4">
              {/* Action Card */}
              <Card className="p-6 border-border/50 bg-gradient-to-b from-card to-muted/20">
                <div className="space-y-5">
                  {/* Quick Stats */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Zap className="w-4 h-4" />
                        Difficulty
                      </div>
                      <span className={`text-sm font-medium ${difficulty.color}`}>
                        {difficulty.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        Time to MVP
                      </div>
                      <span className="text-sm font-medium">
                        {timeToMVP}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Target className="w-4 h-4" />
                        Trend Score
                      </div>
                      <span className="text-sm font-medium">
                        {startup.trend_score}/100
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-border/50" />

                  {/* Primary CTA */}
                  <Button 
                    onClick={handleBuildWithAI}
                    className="w-full gap-2 h-12 text-base font-medium"
                    size="lg"
                  >
                    <Sparkles className="w-5 h-5" />
                    Create with AI
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    AI will generate a complete app based on this idea
                  </p>
                </div>
              </Card>

              {/* Monetization Quick View */}
              <Card className="p-5 border-border/50">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <h3 className="font-medium text-sm">Revenue Potential</h3>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                    Subscription model
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                    Freemium tier
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary/60" />
                    Usage-based pricing
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupProfile;
